import { useEffect, useState, useCallback, useRef } from 'react'
import { hasAuthParams, useAuth } from 'react-oidc-context'
import { Navigate } from 'react-router-dom'
import { ProtectedAppProps } from '../auth.types'
import { useUserPermissions } from '@auth/hooks/useAuthQueries'
import { ROUTES } from 'src/routes'
// We no longer need these imports as the related code is commented out
// import { getUserSessions, logoutOtherSessions } from '../utils/keycloakSessionService'
import { SessionTransferDialog } from './SessionTransferDialog'
import { SessionExpiredDialog } from './SessionExpiredDialog'
import { EsignPopupOverlay } from './EsignPopupOverlay'
import { useSessionValidator } from '../hooks/seSessionValidator'
import axios from 'axios'

// For testing purposes, we expose the internal state
// eslint-disable-next-line react-refresh/only-export-components
export const __TEST_ONLY__ = {
  showSessionDialog: false,
  setShowSessionDialog: (value: boolean) => {
    __TEST_ONLY__.showSessionDialog = value
  },
}

export function ProtectedApp(props: ProtectedAppProps) {
  const { children } = props

  const auth = useAuth()
  // Type assertion needed because the auth object from useAuth() is not fully compatible
  // with the ExtendedAuth type expected by useSessionValidator
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { markSessionTransferred, isActive, validateSessionBeforeAction } = useSessionValidator(auth as any)

  // Set up an effect to catch network errors during session validation
  useEffect(() => {
    const checkSessionWithErrorHandling = async () => {
      try {
        await validateSessionBeforeAction()
      } catch (error) {
        console.error('Error during session validation in ProtectedApp:', error)

        // Check if it's a network error from axios
        if (axios.isAxiosError(error) && error.message === 'Network Error') {
          console.error('Network error detected in ProtectedApp, showing session expired dialog')
          setShowSessionExpiredDialog(true)
        }
      }
    }

    // Only run this check if the user is authenticated
    if (auth.isAuthenticated) {
      checkSessionWithErrorHandling()
    }
  }, [auth.isAuthenticated, validateSessionBeforeAction])

  const [hasTriedSignin, setHasTriedSignin] = useState(false)
  const [showSessionDialog, setShowSessionDialog] = useState(false)
  const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false)
  const [isCheckingSession, setIsCheckingSession] = useState(false)
  const sessionTransferInProgress = useRef(false)

  // Update the test-only state when the real state changes
  useEffect(() => {
    __TEST_ONLY__.showSessionDialog = showSessionDialog
    // This is only for testing, so we don't need to include it in the dependency array
  }, [showSessionDialog])

  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    isError: isErrorPermissions,
  } = useUserPermissions({
    enabled: auth.isAuthenticated,
  })

  // Handle when user clicks "Back to Login" in the session expired dialog
  const handleBackToLogin = useCallback(() => {
    setShowSessionExpiredDialog(false)
    // Redirect to login page
    void auth.signoutRedirect()
  }, [auth])

  // Check if user is already logged in on another browser
  const checkExistingSessions = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user?.profile?.sub || !auth.user?.access_token || !auth.user?.session_state) {
      return
    }

    // Skip session check if a session transfer is in progress
    if (sessionTransferInProgress.current) {
      console.log('Session transfer in progress, skipping session check')
      return
    }

    try {
      setIsCheckingSession(true)
      // These variables are no longer needed as the related code is commented out
      // const userId = auth.user?.profile?.sub
      // const accessToken = auth.user?.access_token

      // Comment out the session API call as we're not detecting user login now
      /*
      const sessions = await getUserSessions(userId, accessToken)

      // If there are multiple sessions, show the dialog
      if (sessions.length > 1) {
        setShowSessionDialog(true)
      }
      */

      // For now, we're not showing the session dialog at all
      console.log('Session check disabled, not showing session dialog')
    } catch (error) {
      console.error('Error checking existing sessions:', error)

      // Check if it's a network error from axios
      if (axios.isAxiosError(error) && error.message === 'Network Error') {
        console.error('Network error detected in getUserSessions, showing session expired dialog')
        setShowSessionExpiredDialog(true)
      }
    } finally {
      setIsCheckingSession(false)
    }
  }, [auth])

  // Handle when user chooses to proceed with the new login
  const handleProceedWithNewLogin = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user?.profile?.sub || !auth.user?.access_token || !auth.user?.session_state) {
      return
    }

    try {
      // These variables are no longer needed as the related code is commented out
      // const userId = auth.user?.profile?.sub
      // const sessionId = auth.user?.session_state
      // const accessToken = auth.user?.access_token

      // Set the flag to indicate session transfer is in progress
      sessionTransferInProgress.current = true

      // Mark that we're transferring the session to prevent auto-logout
      markSessionTransferred()

      // Comment out the logout of other sessions to prevent current user from being logged out
      // This was causing the issue where clicking "Proceed" would log out the current user
      /*
      // Logout all other sessions
      const result = await logoutOtherSessions(userId, sessionId, accessToken)

      console.log('Session logout result:', result)

      if (!result.success) {
        console.warn('Failed to log out all other sessions. Some sessions may still be active.')
        // We'll still close the dialog and continue, but log a warning
      }
      */

      // Just log that we're proceeding without logging out other sessions
      console.log('Proceeding with current session without logging out other sessions')

      // Close the dialog
      setShowSessionDialog(false)

      // Reset the session transfer flag after a delay
      // This ensures that the flag is not persisted indefinitely
      setTimeout(() => {
        sessionTransferInProgress.current = false
      }, 60000) // Reset after 1 minute
    } catch (error) {
      console.error('Error handling session transfer:', error)
      // Reset the flag if there was an error
      sessionTransferInProgress.current = false
    }
  }, [auth, markSessionTransferred])

  // Handle when user cancels the new login
  const handleCancelNewLogin = useCallback(() => {
    setShowSessionDialog(false)
    // Redirect to login page
    void auth.signoutRedirect()
  }, [auth])

  useEffect(() => {
    if (!(hasAuthParams() || auth.isAuthenticated || auth.activeNavigator || auth.isLoading || hasTriedSignin)) {
      void auth.signinRedirect()
      setHasTriedSignin(true)
    }
  }, [auth])

  // Check for existing sessions when user is authenticated
  // Add a delay to ensure the auth process is complete
  useEffect(() => {
    let sessionCheckTimer: number | undefined

    if (auth.isAuthenticated && !isCheckingSession) {
      // Delay the session check to avoid interfering with the login process
      sessionCheckTimer = window.setTimeout(() => {
        try {
          checkExistingSessions()
        } catch (error) {
          console.error('Error during session check:', error)
          // Don't let session check errors break the login flow
          setIsCheckingSession(false)
        }
      }, 2000) // 2 second delay
    }

    return () => {
      if (sessionCheckTimer) {
        window.clearTimeout(sessionCheckTimer)
      }
    }
  }, [auth.isAuthenticated, checkExistingSessions, isCheckingSession])

  // Don't block rendering on session check
  if (auth.isLoading || isLoadingPermissions) {
    return <h1>Loading...</h1>
  }

  if (auth.error || isErrorPermissions) {
    return (
      <div>
        <h1>We've hit a snag</h1>
      </div>
    )
  }

  if (auth.isAuthenticated && permissions) {
    if (permissions.length === 0) {
      return <Navigate to={ROUTES.FORBIDDEN.path} />
    }

    // If the browser is inactive for too long, show a warning banner
    // This is just a visual indicator and doesn't affect functionality
    // The actual session validation happens in useSessionValidator
    const inactivityWarning = !isActive && (
      <div
        style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '10px',
          textAlign: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
        }}
      >
        This browser tab has been inactive for a while. If you were logged out in another browser, you may need to
        refresh.
      </div>
    )

    return (
      <>
        {inactivityWarning}
        <SessionTransferDialog
          open={showSessionDialog}
          onProceed={handleProceedWithNewLogin}
          onCancel={handleCancelNewLogin}
        />
        <SessionExpiredDialog open={showSessionExpiredDialog} onBackToLogin={handleBackToLogin} />
        <EsignPopupOverlay />
        {children}
      </>
    )
  }

  return <h1>We've hit a snag</h1>
}
